import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PPPD_TYPES, PPPD_CATEGORIES } from "@/lib/constants";
import { Loader2, Plus, Trash2, Pencil, X, Layers, ListChecks } from "lucide-react";

const STYLE = {
  Paragraf: { head: "bg-blue-600", soft: "bg-blue-50 border-blue-200" },
  Problem: { head: "bg-amber-500", soft: "bg-amber-50 border-amber-200" },
  Dilbilgisi: { head: "bg-emerald-600", soft: "bg-emerald-50 border-emerald-200" },
  Geometri: { head: "bg-purple-600", soft: "bg-purple-50 border-purple-200" },
};

const agg = (rows) =>
  rows.reduce(
    (a, e) => ({
      total: a.total + Number(e.total || 0),
      correct: a.correct + Number(e.correct || 0),
      wrong: a.wrong + Number(e.wrong || 0),
      blank: a.blank + Number(e.blank || 0),
    }),
    { total: 0, correct: 0, wrong: 0, blank: 0 }
  );

export default function PPPD({ studentId, readOnly }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: "", type: "", category: "", name: "", publisher: "", resource_name: "", total: "", correct: "", wrong: "", blank: "" });
  const [editId, setEditId] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkTargets, setBulkTargets] = useState({ tracks: [], students: [] });
  const [targetData, setTargetData] = useState(null);

  useEffect(() => {
    if (!bulk || targetData) return;
    Promise.all([api.get("/students"), api.get("/meta")])
      .then(([s, m]) => setTargetData({ students: s.data, tracks: m.data.tracks || [] }))
      .catch(() => setTargetData({ students: [], tracks: [] }));
  }, [bulk, targetData]);

  const toggleBulkTrack = (t) =>
    setBulkTargets((p) => {
      if (p.tracks.includes(t)) return { ...p, tracks: p.tracks.filter((x) => x !== t) };
      if (p.tracks.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return p;
      }
      return { ...p, tracks: [...p.tracks, t] };
    });

  const toggleBulkStudent = (id) =>
    setBulkTargets((p) => ({
      ...p,
      students: p.students.includes(id) ? p.students.filter((x) => x !== id) : [...p.students, id],
    }));

  const bulkStudentIds = () => {
    const ids = new Set([studentId, ...bulkTargets.students]);
    (targetData?.students || []).forEach((s) => {
      if (bulkTargets.tracks.includes(s.track)) ids.add(s.id);
    });
    return [...ids];
  };

  const EMPTY_FORM = { date: "", type: "", category: "", name: "", publisher: "", resource_name: "", total: "", correct: "", wrong: "", blank: "" };
  const EMPTY_BULK = { date: "", type: "Paragraf", publisher: "", name: "", count: 10, optik: false, qcount: 40 };

  const saveBulk = async () => {
    const n = Number(bulk.count || 0);
    if (!bulk.name.trim()) return toast.error("Deneme adı zorunlu");
    if (n < 1 || n > 200) return toast.error("Adet 1-200 arası olmalı");
    setBulkSaving(true);
    try {
      const base = bulk.date ? new Date(bulk.date + "T00:00:00") : null;
      const rows = Array.from({ length: n }, (_, i) => {
        const d = base ? new Date(base.getTime() + i * 86400000) : null;
        return {
          date: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "",
          type: bulk.type, category: "Deneme",
          name: `${bulk.name.trim()} ${i + 1}`, publisher: bulk.publisher.trim(), resource_name: bulk.name.trim(),
          total: Number(bulk.qcount) || 0, correct: 0, wrong: 0, blank: Number(bulk.qcount) || 0,
        };
      });
      const { data } = await api.post(`/records-bulk/pppd/${studentId}`, { rows, student_ids: bulkStudentIds() });
      toast.success(`${data.created} deneme ${data.students} öğrenciye oluşturuldu — sonuçları tabloda düzenle`);
      if (bulk.optik) {
        const ids = bulkStudentIds();
        const qc = Math.max(1, Number(bulk.qcount) || 1);
        for (const r of rows) {
          await api.post("/optical-exams", {
            name: r.name, exam_type: "Branş Deneme", date: r.date,
            subjects: [{ name: bulk.type, question_count: qc, answer_key: "", kazanimlar: [] }],
            assigned_tracks: bulkTargets.tracks, assigned_student_ids: ids,
          });
        }
        toast.success("Canlı optik sınavlar da oluşturuldu — cevap anahtarları sonra girilebilir");
      }
      setBulk(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBulkSaving(false);
    }
  };

  const uploadBulkFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBulkSaving(true);
    try {
      const text = await file.text();
      const rows = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const [date, name, publisher, total, correct, wrong, blank] = l.split(";").map((x) => (x || "").trim());
          return {
            date: date || "", type: bulk.type, category: "Deneme", name,
            publisher: publisher || "", resource_name: name,
            total: Number(total) || 0, correct: Number(correct) || 0, wrong: Number(wrong) || 0, blank: Number(blank) || 0,
          };
        })
        .filter((r) => r.name);
      if (!rows.length) return toast.error("Dosyada geçerli satır yok");
      const { data } = await api.post(`/records-bulk/pppd/${studentId}`, { rows, student_ids: bulkStudentIds() });
      toast.success(`${data.created} deneme ${data.students} öğrenciye dosyadan yüklendi`);
      setBulk(null);
      load();
    } catch (e2) {
      toast.error(e2.message || apiErr(e2));
    } finally {
      setBulkSaving(false);
    }
  };

  const [resOpen, setResOpen] = useState(null);
  const [resSaving, setResSaving] = useState(false);

  const openResultEntry = () => {
    const rows = items
      .filter((r) => !Number(r.correct) && !Number(r.wrong))
      .map((r) => ({ ...r, t: "", dc: "", yc: "", bl: "" }));
    if (!rows.length) return toast.info("Sonuç bekleyen deneme yok");
    setResOpen(rows);
  };

  const saveResults = async () => {
    const updates = resOpen
      .filter((r) => r.dc !== "" || r.yc !== "" || r.bl !== "")
      .map((r) => ({
        id: r.id,
        total: Number(r.t || 0) || Number(r.dc || 0) + Number(r.yc || 0) + Number(r.bl || 0),
        correct: Number(r.dc || 0),
        wrong: Number(r.yc || 0),
        blank: Number(r.bl || 0),
      }));
    if (!updates.length) return toast.error("En az bir satır doldurun");
    setResSaving(true);
    try {
      const { data } = await api.put(`/records-bulk/pppd/${studentId}`, { updates });
      toast.success(`${data.updated} deneme sonucu kaydedildi — net otomatik hesaplandı`);
      setResOpen(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setResSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pppd/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.type || !form.category) return toast.error("Tarih, tür ve kategori zorunlu");
    setSaving(true);
    try {
      const payload = {
        ...form,
        total: Number(form.total || 0),
        correct: Number(form.correct || 0),
        wrong: Number(form.wrong || 0),
        blank: Number(form.blank || 0),
      };
      if (editId) {
        await api.put(`/pppd/${studentId}/${editId}`, payload);
        toast.success("Kayıt güncellendi");
        setEditId(null);
      } else {
        await api.post(`/pppd/${studentId}`, payload);
        toast.success("Kayıt eklendi");
      }
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setForm({
      date: r.date || "", type: r.type || "", category: r.category || "", name: r.name || "", publisher: r.publisher || "", resource_name: r.resource_name || "",
      total: r.total ?? "", correct: r.correct ?? "", wrong: r.wrong ?? "", blank: r.blank ?? "",
    });
    setEditId(r.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/pppd/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="pppd-panel">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900">PPD Takibi</h3>
        <p className="text-sm text-slate-500 mt-0.5">Paragraf, Problem, Dilbilgisi ve Geometri ayrı bölümler halinde; her tür için soru ve deneme toplamları.</p>
      </div>

      {!readOnly && (
        <form onSubmit={submit} className="card-ta p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
            <div>
              <label className="ta-label">Tarih</label>
              <input type="date" className="ta-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pppd-date" />
            </div>
            <div>
              <label className="ta-label">Tür</label>
              <select className="ta-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="pppd-type">
                <option value="">Seç</option>
                {PPPD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="ta-label">Kategori</label>
              <select className="ta-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pppd-category">
                <option value="">Seç</option>
                {PPPD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="ta-label">Deneme Adı</label>
              <input className="ta-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pppd-name" placeholder="Örn: TYT Deneme 5" />
            </div>
            <div>
              <label className="ta-label">Yayınevi</label>
              <input className="ta-input" value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })} data-testid="pppd-publisher" placeholder="Örn: Limit" />
            </div>
            <div>
              <label className="ta-label">Kaynak Adı</label>
              <input className="ta-input" value={form.resource_name} onChange={(e) => setForm({ ...form, resource_name: e.target.value })} data-testid="pppd-resource" placeholder="Örn: Limit TYT" />
            </div>
            <div>
              <label className="ta-label">Toplam</label>
              <input type="number" className="ta-input" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="pppd-total" />
            </div>
            <div>
              <label className="ta-label">Doğru</label>
              <input type="number" className="ta-input" value={form.correct} onChange={(e) => setForm({ ...form, correct: e.target.value })} data-testid="pppd-correct" />
            </div>
            <div>
              <label className="ta-label">Yanlış</label>
              <input type="number" className="ta-input" value={form.wrong} onChange={(e) => setForm({ ...form, wrong: e.target.value })} data-testid="pppd-wrong" />
            </div>
            <div>
              <label className="ta-label">Boş</label>
              <input type="number" className="ta-input" value={form.blank} onChange={(e) => setForm({ ...form, blank: e.target.value })} data-testid="pppd-blank" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button type="submit" disabled={saving} className={editId ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-800 hover:bg-slate-900"} data-testid="pppd-add-btn">
              {saving ? <Loader2 className="animate-spin" size={15} /> : editId ? <Pencil size={15} /> : <Plus size={15} />}<span className="ml-1">{editId ? "Güncelle" : "Kayıt Ekle"}</span>
            </Button>
            {editId && (
              <Button type="button" variant="outline" onClick={cancelEdit} data-testid="pppd-cancel-edit">
                <X size={15} /><span className="ml-1">Vazgeç</span>
              </Button>
            )}
            {!editId && (
              <Button type="button" onClick={() => { setBulk(EMPTY_BULK); setBulkTargets({ tracks: [], students: [] }); }} className="bg-violet-600 hover:bg-violet-700" data-testid="pppd-bulk-btn">
                <Layers size={15} /><span className="ml-1">Toplu Deneme Ekle</span>
              </Button>
            )}
            {!editId && (
              <Button type="button" onClick={openResultEntry} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pppd-results-btn">
                <ListChecks size={15} /><span className="ml-1">Toplu Sonuç Gir</span>
              </Button>
            )}
          </div>
        </form>
      )}

      {bulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setBulk(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="pppd-bulk-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Layers size={18} className="text-violet-600" /> Toplu Deneme Ekle</h3>
              <button onClick={() => setBulk(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Örn: "3D Dilbilgisi Denemesi" + Adet 30 → tabloda <b>3D Dilbilgisi Denemesi 1…30</b> oluşur, sonuçları tek tek düzenlersin. Tarih girersen denemeler <b>art arda günlere</b> otomatik dağıtılır (1. deneme seçilen gün, sonrakiler ertesi günler).</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="ta-label">Deneme Adı</label>
                <input className="ta-input" value={bulk.name} onChange={(e) => setBulk({ ...bulk, name: e.target.value })} data-testid="pppd-bulk-name" placeholder="Örn: 3D Dilbilgisi Denemesi" />
              </div>
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={bulk.publisher} onChange={(e) => setBulk({ ...bulk, publisher: e.target.value })} data-testid="pppd-bulk-publisher" placeholder="Örn: 3D Yayınları" />
              </div>
              <div>
                <label className="ta-label">Tür</label>
                <select className="ta-input" value={bulk.type} onChange={(e) => setBulk({ ...bulk, type: e.target.value })} data-testid="pppd-bulk-type">
                  {PPPD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="ta-label">Adet</label>
                <input type="number" min="1" max="200" className="ta-input" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} data-testid="pppd-bulk-count" />
              </div>
              <div>
                <label className="ta-label">Soru Sayısı</label>
                <input type="number" min="0" className="ta-input text-center" value={bulk.qcount} onChange={(e) => setBulk({ ...bulk, qcount: e.target.value })} data-testid="pppd-bulk-qcount" />
              </div>
              <div>
                <label className="ta-label">Başlangıç Tarihi (ops. — art arda günlere dağıtılır)</label>
                <input type="date" className="ta-input" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} data-testid="pppd-bulk-date" />
              </div>
              {targetData && targetData.students.length > 0 && (
                <div className="col-span-2 border-t border-slate-100 pt-3 space-y-2" data-testid="pppd-bulk-targets">
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                    <input type="checkbox" id="pppd-optik" className="accent-indigo-700" checked={bulk.optik} onChange={(e) => setBulk({ ...bulk, optik: e.target.checked })} data-testid="pppd-bulk-optik" />
                    <label htmlFor="pppd-optik" className="text-xs text-indigo-800 font-semibold">Canlı Optik sınavı da oluştur (zorunlu değil — soru sayısı yukarıdan alınır, cevap anahtarı sonra girilebilir)</label>
                  </div>
                  <label className="ta-label">Hedef — birden fazla sınıf/alan ve öğrenci seçebilirsiniz (varsayılan: seçili öğrenci)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {targetData.tracks.map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => toggleBulkTrack(t)}
                        data-testid={`pppd-bulk-track-${t}`}
                        className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                          bulkTargets.tracks.includes(t) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-28 overflow-y-auto grid grid-cols-2 gap-1 bg-white border border-slate-200 rounded-lg p-2">
                    {targetData.students
                      .filter((s) => !bulkTargets.tracks.length || bulkTargets.tracks.includes(s.track))
                      .map((s) => (
                        <label key={s.id} className="flex items-center gap-1.5 text-xs text-slate-700 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`pppd-bulk-stu-${s.id}`}>
                          <input type="checkbox" className="accent-violet-600" checked={bulkTargets.students.includes(s.id)} onChange={() => toggleBulkStudent(s.id)} />
                          {s.name} {s.track ? <span className="text-slate-400">· {s.track}</span> : null}
                        </label>
                      ))}
                  </div>
                  <div className="text-[11px] text-violet-600 font-semibold">
                    {bulkStudentIds().length} öğrenciye eklenecek
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <label className="ta-label">Veya Dosya ile Toplu Yükle (.csv / .txt)</label>
              <p className="text-[11px] text-slate-400 mb-2">
                Her satır: <code className="bg-slate-50 px-1 rounded border border-slate-200 font-mono">tarih;deneme_adı;yayınevi;toplam;doğru;yanlış;boş</code>
              </p>
              <input type="file" accept=".csv,.txt" onChange={uploadBulkFile} disabled={bulkSaving} className="text-xs text-slate-600" data-testid="pppd-bulk-file" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulk(null)}>Vazgeç</Button>
              <Button onClick={saveBulk} disabled={bulkSaving} className="bg-violet-600 hover:bg-violet-700" data-testid="pppd-bulk-save">
                {bulkSaving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Oluştur</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : (
        PPPD_TYPES.map((t) => {
          const rows = items.filter((i) => i.type === t);
          const soru = agg(rows.filter((r) => r.category === "Soru"));
          const deneme = agg(rows.filter((r) => r.category === "Deneme"));
          const S = STYLE[t];
          return (
            <div key={t} className="card-ta overflow-hidden" data-testid={`pppd-section-${t}`}>
              <div className={`${S.head} text-white px-4 py-2.5 flex items-center justify-between`}>
                <span className="font-display font-bold tracking-wide">{t.toUpperCase()}</span>
                <span className="text-xs font-semibold opacity-90">{rows.length} kayıt</span>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[{ label: "Soru Toplamı", a: soru }, { label: "Deneme Toplamı", a: deneme }].map((blk) => (
                    <div key={blk.label} className={`rounded-lg border p-3 ${S.soft}`}>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t} {blk.label}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
                        <span>Top: <b>{blk.a.total}</b></span>
                        <span className="text-emerald-700">D: <b>{blk.a.correct}</b></span>
                        <span className="text-rose-700">Y: <b>{blk.a.wrong}</b></span>
                        <span>Boş: <b>{blk.a.blank}</b></span>
                        <span className="text-indigo-700">Net: <b>{(blk.a.correct - blk.a.wrong / 4).toFixed(2)}</b></span>
                      </div>
                    </div>
                  ))}
                </div>
                {rows.length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-2">Bu türde kayıt yok.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-slate-500">
                          <th className="px-3 py-2 font-medium">Tarih</th>
                          <th className="px-3 py-2 font-medium">Kategori</th>
                          <th className="px-3 py-2 font-medium">Deneme</th>
                          <th className="px-3 py-2 font-medium">Yayın</th>
                          <th className="px-3 py-2 font-medium">Kaynak</th>
                          <th className="px-3 py-2 font-medium text-right">Toplam</th>
                          <th className="px-3 py-2 font-medium text-right">D</th>
                          <th className="px-3 py-2 font-medium text-right">Y</th>
                          <th className="px-3 py-2 font-medium text-right">Boş</th>
                          <th className="px-3 py-2 font-medium text-right">Net</th>
                          {!readOnly && <th className="px-3 py-2" />}
                        </tr>
                      </thead>
                      <tbody>
                        {[...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((r) => (
                          <tr key={r.id} className="border-b border-slate-100">
                            <td className="px-3 py-2">{r.date}</td>
                            <td className="px-3 py-2">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${r.category === "Deneme" ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.category}</span>
                            </td>
                            <td className="px-3 py-2 font-medium text-slate-800">{r.name || "-"}</td>
                            <td className="px-3 py-2 text-indigo-600 text-xs font-semibold">{r.publisher || "-"}</td>
                            <td className="px-3 py-2 text-slate-600">{r.resource_name || "-"}</td>
                            <td className="px-3 py-2 text-right font-semibold">{r.total}</td>
                            <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{r.correct}</td>
                            <td className="px-3 py-2 text-right text-rose-700 font-semibold">{r.wrong}</td>
                            <td className="px-3 py-2 text-right text-slate-500">{r.blank ?? 0}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-indigo-700">{Number(r.net || 0).toFixed(2)}</td>
                            {!readOnly && (
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <button onClick={() => startEdit(r)} className="text-slate-300 hover:text-amber-600 mr-3" data-testid={`pppd-edit-${r.id}`}><Pencil size={14} /></button>
                                <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-rose-600" data-testid={`pppd-del-${r.id}`}><Trash2 size={14} /></button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {resOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setResOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="pppd-results-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><ListChecks size={18} className="text-emerald-600" /> Toplu Sonuç Gir (elle)</h3>
              <button onClick={() => setResOpen(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Sonuç bekleyen denemeler listelenir. Toplam boş bırakılırsa D+Y+B toplamı alınır; net otomatik hesaplanır ve tüm panellerde görünür.</p>
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 text-[11px] font-bold text-slate-400 uppercase px-1">
                <span>Deneme</span><span className="text-center">Toplam</span><span className="text-center">D</span><span className="text-center">Y</span><span className="text-center">Boş</span>
              </div>
              {resOpen.map((r) => (
                <div key={r.id} className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 items-center bg-slate-50 rounded-lg px-2 py-1.5">
                  <div className="text-xs font-semibold text-slate-700 truncate">{r.name}<span className="text-slate-400 font-normal"> · {r.date || "-"}</span></div>
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.t} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, t: e.target.value } : x)))} data-testid={`res-t-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center border-emerald-200" value={r.dc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, dc: e.target.value } : x)))} data-testid={`res-d-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center border-rose-200" value={r.yc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, yc: e.target.value } : x)))} data-testid={`res-y-${r.id}`} />
                  <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.bl} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, bl: e.target.value } : x)))} data-testid={`res-b-${r.id}`} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResOpen(null)}>Vazgeç</Button>
              <Button onClick={saveResults} disabled={resSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pppd-results-save">
                {resSaving ? <Loader2 className="animate-spin" size={15} /> : <ListChecks size={15} />}<span className="ml-1">Sonuçları Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
